<template lang="pug">
section.bulk-send-failed-entries.d-flex.flex-column.gap-3.p-4.bg-white.border.rounded(
  v-if='total > 0'
)
  .d-flex.align-items-start.justify-content-between.flex-wrap.gap-3
    .d-flex.flex-column.gap-1
      h2.fs-6.fw-bold.text-body.m-0
        | {{ $t('bulkSendDetails.failedEntries.title') }}
        span.badge.rounded-pill.failed-badge.ms-2 {{ total }}
      small.text-muted {{ $t('bulkSendDetails.failedEntries.description') }}
    button.collapse-button.btn(
      :aria-expanded='String(!collapsed)'
      :title='collapsed ? $t("bulkSendDetails.failedEntries.expand") : $t("bulkSendDetails.failedEntries.collapse")'
      @click='collapsed = !collapsed'
      type='button'
    )
      icon(:class='{ collapsed }' name='chevron-down')

  .d-flex.flex-column.gap-3(v-if='!collapsed')
    button.retry-button.btn.btn-form-submit.d-flex.align-items-center.gap-2.align-self-end(
      :disabled='retryLoading'
      @click='retry'
      type='button'
      v-if='canRetry'
    )
      icon(name='arrow-rotate-right')
      | {{ $t('bulkSendDetails.failedEntries.retry', { total }) }}
    card-table(:total-items='entries.data.length' breakpoint-size='lg')
      template(
        v-for='(item, index) in entries.data'
        v-slot:[`card-${index}`]='{}'
      )
        .mb-1.text-break(
          :key='recipient.prefix'
          v-for='recipient in getRecipients(item.data)'
        )
          | {{ $t('bulkSendDetails.table.recipients') }}: {{ recipient.name || '—' }} ({{ recipient.contact || '—' }})
        .mb-1.failure-reason
          | {{ $t('bulkSendDetails.failedEntries.reason') }}: {{ item.failure_reason || $t('bulkSendDetails.failedEntries.unknownReason') }}
      template(v-slot:table-head)
        th(style='width: 50%') {{ $t('bulkSendDetails.table.recipients') }}
        th(style='width: 50%') {{ $t('bulkSendDetails.failedEntries.reason') }}
      template(
        v-for='(item, index) in entries.data'
        v-slot:[`table-row-${index}`]='{}'
      )
        td
          .d-flex.flex-column.gap-2
            .d-flex.flex-column(
              :key='recipient.prefix'
              v-for='recipient in getRecipients(item.data)'
            )
              span {{ recipient.name || '—' }}
              small.text-muted.text-break {{ recipient.contact || '—' }}
        td.failure-reason
          | {{ item.failure_reason || $t('bulkSendDetails.failedEntries.unknownReason') }}

    simple-paginator(:payload='entries' @navigate='fetchEntries')
</template>

<script>
import Icon from '@/ui/atoms/Icon.vue';
import CardTable from '@/ui/organisms/CardTable.vue';
import SimplePaginator from '@/ui/organisms/SimplePaginator.vue';
import EnvelopesService from '@/modules/business/services/http/envelopes/EnvelopesService';
import BulkSendEntryStatus from '@/enums/BulkSendEntryStatus';
import BulkSendService from '@/modules/business/services/BulkSendService';
import alert from '@/modules/account/common/alert';

export default {
  name: 'BulkSendFailedEntries',
  components: {
    Icon,
    CardTable,
    SimplePaginator,
  },
  props: {
    bulkSendUuid: {
      type: String,
      required: true,
    },
    canRetry: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      entries: {
        data: [],
        links: {},
        meta: {},
      },
      retryLoading: false,
      collapsed: false,
    };
  },
  computed: {
    total() {
      return this.entries.meta.total || 0;
    },
  },
  created() {
    this.fetchEntries();
  },
  methods: {
    async fetchEntries(page = 1) {
      try {
        const { data } = await EnvelopesService.listBulkSendEntries(
          this.bulkSendUuid,
          { 'filter[status]': BulkSendEntryStatus.FAILED, page }
        );

        this.entries = data;
      } catch {
        alert.fireAlert(this.$t('bulkSendDetails.failedEntries.listError'), {
          classes: 'alert-danger',
          styles:
            'background-color: #f8d7da; border-color: #F5C6CB; color: #721C24;',
          tag: 'httpAlert',
          icon: 'triangle-exclamation',
        });
      }
    },
    async retry() {
      this.retryLoading = true;

      try {
        await EnvelopesService.retryBulkSend(this.bulkSendUuid);
        alert.fireAlert(
          this.$t('bulkSendDetails.failedEntries.retrySuccess', {
            total: this.total,
          }),
          {
            classes: 'alert-success',
            styles:
              'background-color: #d4edda; border-color: #c3e6cb; color: #155724;',
            icon: 'circle-check',
          }
        );
        this.$emit('retried');
        await this.fetchEntries();
      } catch (error) {
        const message =
          (error.response && error.response.data.message) ||
          this.$t('bulkSendDetails.failedEntries.retryError');

        alert.fireAlert(message, {
          classes: 'alert-danger',
          styles:
            'background-color: #f8d7da; border-color: #F5C6CB; color: #721C24;',
          tag: 'httpAlert',
          icon: 'triangle-exclamation',
        });
      } finally {
        this.retryLoading = false;
      }
    },
    getRecipients(data) {
      return BulkSendService.getRecipients(data);
    },
  },
};
</script>

<style lang="stylus" scoped>
.failed-badge
  background-color: #FBE9E7
  color: #C0392B

.failure-reason
  color: #C0392B

.retry-button
  width: auto

.collapse-button
  border: 0
  color: #1284C9
  .icon
    transition: transform 0.2s
    &.collapsed
      transform: rotate(-90deg)
</style>
